/**
 * card-size-pills.js — Dawn Theme
 * ─────────────────────────────────────────────────────────────
 * Reads each product card's variant JSON (injected via Liquid),
 * builds size pill elements, and appends them to the card image.
 *
 * HOW IT WORKS
 * 1. Each card-product snippet outputs a <script type="application/json">
 *    block containing variant data (added by you in card-product.liquid).
 * 2. This script reads that JSON, figures out which size options exist,
 *    checks availability per size, and renders pills.
 * 3. Clicking a pill navigates to the product URL with ?variant=ID so
 *    the correct size is pre-selected on the PDP.
 * 4. On touch devices a tap on the card reveals pills; a second tap
 *    on a specific pill follows the link.
 */

(function () {
  'use strict';

  // ── Helpers ────────────────────────────────────────────────

  /**
   * Find the option index that represents "Size" in the product's options.
   * Matches common labels: Size, size, Taille, Größe, etc.
   */
  function getSizeOptionIndex(options) {
    const sizeKeywords = [
      'size', 'taille', 'taglia', 'größe', 'tamaño', 'maat',
      'साइज़', 'размер', 'taille', 'storlek', 'koko'
    ];
    for (let i = 0; i < options.length; i++) {
      if (sizeKeywords.includes(options[i].toLowerCase())) {
        return i;
      }
    }
    // Fallback: if there's only one option, it's almost certainly size
    if (options.length === 1) return 0;
    return -1;
  }

  /**
   * Return the size value for a variant based on the option index.
   */
  function getVariantSizeValue(variant, optionIndex) {
    return variant['option' + (optionIndex + 1)];
  }

  /**
   * Build the ordered list of unique sizes, preserving natural order
   * (as they appear in variants) and noting availability.
   */
  function buildSizeMap(variants, optionIndex) {
    const map = new Map(); // label → { available: bool, variantId: number, url: string }

    variants.forEach(function (v) {
      const label = getVariantSizeValue(v, optionIndex);
      if (!label) return;

      if (!map.has(label)) {
        map.set(label, { available: v.available, variantId: v.id });
      } else if (v.available && !map.get(label).available) {
        // Prefer an available variant for the link
        map.set(label, { available: true, variantId: v.id });
      }
    });

    return map;
  }

  /**
   * Create pill anchor element.
   */
  function createPill(label, info, productUrl) {
    const a = document.createElement('a');
    a.className = 'size-pill' + (info.available ? '' : ' size-pill--sold-out');
    a.textContent = label;
    a.setAttribute('aria-label', label + (info.available ? '' : ' — sold out'));

    if (info.available) {
      // Pre-select variant on the PDP.
      // Use URL + URLSearchParams so we never double-up on '?' if the
      // product URL already carries query params (e.g. collection context).
      var base = productUrl.split('?')[0]; // strip any existing params
      a.href = base + '?variant=' + info.variantId;
    } else {
      a.removeAttribute('href');
      a.setAttribute('role', 'presentation');
    }

    // Prevent card-level touch handler from firing when tapping an actual pill
    a.addEventListener('click', function (e) {
      e.stopPropagation();
    });

    return a;
  }

  /**
   * Build and inject the pill container into a card's media element.
   */
  function buildPillsForCard(card) {
    // Avoid double-initialisation
    if (card.dataset.sizePillsReady) return;
    card.dataset.sizePillsReady = 'true';

    const dataEl = card.querySelector('script[data-size-pills-data]');
    if (!dataEl) return;

    let productData;
    try {
      productData = JSON.parse(dataEl.textContent);
    } catch (err) {
      return; // malformed JSON — skip silently
    }

    const { options, variants, url } = productData;
    if (!options || !variants || !url) return;

    const optionIndex = getSizeOptionIndex(options);
    if (optionIndex === -1) return; // product has no size option

    const sizeMap = buildSizeMap(variants, optionIndex);
    if (sizeMap.size === 0) return;

    // Container
    const pillsContainer = document.createElement('div');
    pillsContainer.className = 'card-size-pills';
    pillsContainer.setAttribute('aria-label', 'Available sizes');

    sizeMap.forEach(function (info, label) {
      pillsContainer.appendChild(createPill(label, info, url));
    });

    // Inject at the end of .card__media so it overlays the image
    const mediaEl = card.querySelector('.card__media');
    if (mediaEl) {
      mediaEl.appendChild(pillsContainer);
    }
  }

  // ── Mobile touch handling ───────────────────────────────────

  function setupTouchHandling(card) {
    card.addEventListener('touchstart', function (e) {
      var tappedPill = e.target.closest('.size-pill');

      if (card.classList.contains('touch-active')) {
        // Pills are already visible.
        // If the user tapped a pill, let the browser follow the <a> href naturally —
        // do NOT return early or call preventDefault; just close other cards.
        if (tappedPill) {
          // Close other open cards but let this pill's default click/navigate proceed.
          document.querySelectorAll('.card-wrapper.touch-active').forEach(function (other) {
            if (other !== card) other.classList.remove('touch-active');
          });
          return; // pill handles its own navigation via href
        }
        // Tapped somewhere else on the card — hide pills.
        card.classList.remove('touch-active');
        return;
      }

      // First tap on the card: reveal pills (don't navigate yet).
      // Only intercept if the tap wasn't directly on a pill already.
      if (!tappedPill) {
        card.classList.add('touch-active');
        // Hide other open cards.
        document.querySelectorAll('.card-wrapper.touch-active').forEach(function (other) {
          if (other !== card) other.classList.remove('touch-active');
        });
      }
    }, { passive: true });
  }

  // ── Initialisation ─────────────────────────────────────────

  /**
   * CHANGED: accepts an optional root element so we can scope the query
   * to just the newly injected recommendation cards instead of re-scanning
   * the entire document (which would be harmless but wasteful).
   *
   * When called with no argument (or with document) it behaves exactly
   * as the original init() — scanning the full page.
   */
  function init(root) {
    root = root || document;
    var cards = root.querySelectorAll('.card-wrapper[data-product-handle]');

    cards.forEach(function (card) {
      buildPillsForCard(card);
      setupTouchHandling(card);
    });
  }

  // Close touch-active state when tapping outside any card
  document.addEventListener('touchstart', function (e) {
    if (!e.target.closest('.card-wrapper')) {
      document.querySelectorAll('.card-wrapper.touch-active').forEach(function (card) {
        card.classList.remove('touch-active');
      });
    }
  }, { passive: true });

  // Run after DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run after Shopify section AJAX updates (theme editor, etc.)
  document.addEventListener('shopify:section:load', init);

  // Re-run after Dawn's filter / pagination AJAX updates
  document.addEventListener('renderProductGrid', function () {
    setTimeout(init, 100);
  });

  // ── CHANGED: re-run after product-recommendations AJAX injection ──
  // The <product-recommendations> custom element fetches HTML and injects
  // it into the DOM after page load. By that time the init() above has
  // already run and will never see those cards. We listen for the custom
  // event that product-recommendations.js dispatches when it's done, and
  // scope the re-init to just that element so we don't re-process cards
  // that already have pills.
  document.addEventListener('recommendations:loaded', function (e) {
    // e.target is the <product-recommendations> element
    init(e.target);
  });

  // Expose init publicly so other scripts can call it if needed
  window.CardSizePills = { init: init };

})();